import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry from 'react-masonry-css';
import { LoadingSpinner } from '@/components';
import { useViewModeContext } from '@/context/ViewModeContext';
import { useIsMobile } from '@/hooks';
import { INFINITE_SCROLL_DESKTOP_MIN_PX } from '@/config/constants';
import { useEffect, useState } from 'react';

type Props<T> = {
  items: T[];
  total: number;
  nextPage: () => void;
  renderItem: (item: T) => React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

export function FlyerMasonryInfinite<T>({
  items,
  total,
  nextPage,
  renderItem,
  className,
  style,
}: Props<T>) {
  const { masonryBreakpointCols } = useViewModeContext();
  const isMobile = useIsMobile();
  const dataLength = items.length;
  const hasMore = dataLength < total;
  const computeDesktopThresholdPx = () =>
    `${Math.max(Math.floor(window.innerHeight * 0.5), INFINITE_SCROLL_DESKTOP_MIN_PX)}px`;
  const [infiniteScrollThreshold, setInfiniteScrollThreshold] = useState<string | number>(
    isMobile ? 0.6 : computeDesktopThresholdPx()
  );

  useEffect(() => {
    if (isMobile) {
      setInfiniteScrollThreshold(0.6);
      return;
    }
    const handleResize = () => setInfiniteScrollThreshold(computeDesktopThresholdPx());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  if (dataLength === 0) return null;

  return (
    <InfiniteScroll
      style={style}
      dataLength={dataLength}
      next={nextPage}
      hasMore={hasMore}
      scrollThreshold={infiniteScrollThreshold}
      loader={<LoadingSpinner />}
      className={className}
    >
      <Masonry
        breakpointCols={masonryBreakpointCols}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
        role="list"
      >
        {items.map((item, index) => (
          <div key={index} role="listitem">
            {renderItem(item)}
          </div>
        ))}
      </Masonry>
    </InfiniteScroll>
  );
}


